import React from "react";
import { Box, Typography, Container, Grid } from "@mui/material";
import { useNavigate } from "react-router-dom";
import BarChartIcon from "@mui/icons-material/BarChart";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import { motion } from "framer-motion";
import GlassPanel from "./ui/GlassPanel";
import MagneticButton from "./ui/MagneticButton";
import GradientMeshBackground from "./ui/GradientMeshBackground";
import TiltCard from "./ui/TiltCard";

export default function Hero5() {
  const navigate = useNavigate();

  const statistics = [
    {
      icon: <BarChartIcon sx={{ fontSize: 36, color: "#FD8112" }} />,
      number: "Results That Matter",
      title: "Marketing Options",
      description:
        "Our ROI-driven approach ensures every strategy delivers measurable growth, turning clicks into customers and investments into success.",
    },
    {
      icon: <VisibilityIcon sx={{ fontSize: 36, color: "#FD8112" }} />,
      number: "Vision",
      title: "Increased Visibility",
      description:
        "Businesses struggle to get noticed and grow online. We solve this with trusted digital marketing, data-driven strategies, and real results.",
    },
    {
      icon: <MenuBookIcon sx={{ fontSize: 36, color: "#FD8112" }} />,
      number: "Mission",
      title: "Service Categories",
      description:
        "Facing low visibility, weak engagement, and poor conversions? We solve this with targeted strategies, data-driven campaigns, and performance-focused digital marketing.",
    },
  ];

  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        overflow: "hidden",
        py: { xs: 8, md: 12 },
        background: "linear-gradient(160deg, #0B0B14 0%, #17111C 55%, #241511 100%)",
      }}
    >
      <GradientMeshBackground
        blobs={[
          { size: 480, top: '-15%', left: '-8%', color: 'rgba(253,129,18,0.35)', duration: 20 },
          { size: 340, top: '60%', left: '78%', color: 'rgba(255,95,109,0.28)', duration: 24 },
        ]}
      />
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Typography
            component="h2"
            variant="h3"
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontWeight: 700,
              color: "#fff",
              mb: 2,
            }}
          >
            We Drive Growth for Your Business
          </Typography>

          <Typography
            variant="h6"
            sx={{
              fontFamily: "Nunito, serif",
              color: "rgba(255,255,255,0.7)",
              maxWidth: "800px",
              mx: "auto",
              lineHeight: 1.6,
              fontWeight: 400,
            }}
          >
            Discover the impact of our data-driven marketing strategies through key performance indicators.
          </Typography>
        </Box>

        {/* Statistic Cards */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          {statistics.map((stat, index) => (
            <Grid item xs={12} md={4} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                style={{ height: "100%" }}
              >
                <TiltCard glareColor="#FD8112">
                  <GlassPanel
                    tone="dark"
                    sx={{
                      p: 4,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      position: "relative",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        borderRadius: "inherit",
                        border: "1px solid transparent",
                        background: "linear-gradient(135deg, rgba(253,129,18,0.6), rgba(255,95,109,0.2)) border-box",
                        WebkitMask: "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)",
                        WebkitMaskComposite: "xor",
                        maskComposite: "exclude",
                        opacity: 0,
                        transition: "opacity 0.4s ease",
                      },
                      "&:hover::before": { opacity: 1 },
                      "&:hover": { boxShadow: "0 24px 55px rgba(253,129,18,0.25)" },
                      transition: "box-shadow 0.4s ease",
                    }}
                  >
                    <Box
                      sx={{
                        mb: 2,
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "radial-gradient(circle, rgba(253,129,18,0.22) 0%, rgba(253,129,18,0.05) 70%)",
                        boxShadow: "0 0 0 1px rgba(253,129,18,0.25), 0 12px 24px rgba(253,129,18,0.15)",
                      }}
                    >
                      {stat.icon}
                    </Box>

                    <Typography
                      variant="h5"
                      sx={{
                        fontFamily: "Poppins, sans-serif",
                        fontWeight: 700,
                        color: "#fff",
                        mb: 1,
                        fontSize: "1.4rem",
                      }}
                    >
                      {stat.number}
                    </Typography>

                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontFamily: "Poppins, sans-serif",
                        fontWeight: 600,
                        color: "#FFA954",
                        mb: 2,
                        fontSize: "0.95rem",
                      }}
                    >
                      {stat.title}
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{
                        fontFamily: "Nunito, serif",
                        color: "rgba(255,255,255,0.7)",
                        lineHeight: 1.6,
                      }}
                    >
                      {stat.description}
                    </Typography>
                  </GlassPanel>
                </TiltCard>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Call to Action Button */}
        <Box sx={{ textAlign: "center" }}>
          <MagneticButton
            variant="contained"
            onClick={() => navigate("/")}
            sx={{
              background: "linear-gradient(135deg, #FD8112, #FF5F6D)",
              color: "#fff",
              px: 4,
              py: 1.5,
              fontSize: "1.05rem",
              fontFamily: "Nunito, serif",
            }}
          >
            See Our Success!!
          </MagneticButton>
        </Box>
      </Container>
    </Box>
  );
}
